import { type Clips } from '@air/api';
import { SortDirection } from '@air/api/types';

import { PageLimits } from '~/constants/PageLimits';

/** Add all to canvas refuses boards with more direct assets than this. */
export const MAX_BOARD_CLIPS_FOR_CANVAS = 200;

export type FetchAllBoardClipsParams = {
  boardId: string;
  search: typeof Clips.search;
  workspaceId: string;
};

export type FetchAllBoardClipsResult = {
  /** Empty when `exceedsCap` is true: the caller must not go forward with a board that is over the cap. */
  clips: Awaited<ReturnType<typeof Clips.search>>['data']['clips'];
  exceedsCap: boolean;
  /** The board's asset count as the search reports it, or the clips seen so far if paging overran the cap first. */
  total: number;
};

/**
 * Loads the direct assets on a board (`Clips.search` with `filters.board`, the same filter the board gallery sends for a direct board, never `boardTree`).
 * The first page carries the board's total, so a board over {@link MAX_BOARD_CLIPS_FOR_CANVAS} returns after one request.
 * Paging still stops on an empty page, a cursor that repeats, or a page with no new ids, and keeps the first copy of each clip id.
 */
export const fetchAllBoardClips = async ({
  boardId,
  search,
  workspaceId,
}: FetchAllBoardClipsParams): Promise<FetchAllBoardClipsResult> => {
  const clips: FetchAllBoardClipsResult['clips'] = [];
  const seenClipIds = new Set<string>();
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  let total = 0;

  for (;;) {
    const page = await search({
      boardId,
      cursor,
      filters: { board: { is: boardId } },
      limit: PageLimits.clips.more,
      sortField: { direction: SortDirection.asc, name: 'name' },
      workspaceId,
    });

    total = Math.max(total, page.data.total);

    if (total > MAX_BOARD_CLIPS_FOR_CANVAS) {
      return { clips: [], exceedsCap: true, total };
    }

    if (page.data.clips.length === 0) {
      break;
    }

    const sizeBefore = clips.length;

    for (const clip of page.data.clips) {
      if (seenClipIds.has(clip.id)) {
        continue;
      }

      seenClipIds.add(clip.id);
      clips.push(clip);
    }

    if (clips.length > MAX_BOARD_CLIPS_FOR_CANVAS) {
      return { clips: [], exceedsCap: true, total: clips.length };
    }

    if (clips.length === sizeBefore) {
      break;
    }

    const nextCursor = page.pagination.cursor || undefined;

    if (!nextCursor || seenCursors.has(nextCursor)) {
      break;
    }

    seenCursors.add(nextCursor);
    cursor = nextCursor;
  }

  return { clips, exceedsCap: false, total: Math.max(total, clips.length) };
};
